import type { FC } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData, graphql } from '@packages/gql/generated/shopping';
import { Box, DevelopmentPlaceholder, Typography } from '@packages/shared';

import { InspiringSearchMoreCard } from '../InspiringSearchMoreCard';
import { useOriginalSearchQuery } from '../../hooks/useOriginalSearchQuery';

export const InspiringSearchPreviewFragment = graphql(/* GraphQL */ `
  fragment InspiringSearchPreviewFragment on SuggestionPreviewSearchResult {
    suggestions {
      items {
        styleId
      }
      query
      ...InspiringSearchMoreCardFragment
    }
  }
`);

export type InspiredSearchPreviewProps = {
  maskedData: FragmentType<typeof InspiringSearchPreviewFragment>;
};

const messages = defineMessages({
  header: {
    id: 'search.inspiringSearch.previewPage.header',
    defaultMessage: 'Suchvorschläge für',
    description: 'Inspired search preview page header',
  },
});

/**
 * Preview page for AI-suggested search queries, listing multiple options to continue the user journey after the initial natural language query.
 * */
export const InspiringSearchPreview: FC<InspiredSearchPreviewProps> = ({ maskedData }) => {
  const { suggestions } = getFragmentData(InspiringSearchPreviewFragment, maskedData);

  const { formatMessage } = useIntl();

  const originalQuery = useOriginalSearchQuery();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', marginLeft: 2, marginRight: 2 }}>
      <Typography variant="body2" sx={{ alignSelf: 'center', marginTop: 2.5 }}>
        {formatMessage(messages.header)}
      </Typography>
      <Typography variant="body2" sx={{ alignSelf: 'center', marginBottom: 3 }}>
        „{originalQuery}“
      </Typography>
      {suggestions.map((suggestion) => (
        <>
          <Typography variant="h4">{suggestion.query}</Typography>
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              marginTop: 1,
              marginBottom: 4,
              gridTemplateColumns: {
                xs: `repeat(2, 1fr)`,
                md: `repeat(3, 1fr)`,
                xl: `repeat(4, 1fr)`,
              },
            }}
          >
            {suggestion.items.map((item) => (
              <DevelopmentPlaceholder key={item.styleId} size={3} label={`${item.styleId}`} />
            ))}
            <InspiringSearchMoreCard maskedSuggestion={suggestion} />
          </Box>
        </>
      ))}
    </Box>
  );
};
